import type { AnalysisStatus, Platform } from "@amtana/analysis-contract";
import type { Tone } from "@amtana/design-system";
import type { Catalogs, PlainKey, Translate, Translation } from "@amtana/i18n/catalog";

// Labels, tones and copy the analysis screens use, in English and Spanish.
// `useAnalysisCopy` (behavior/use-analysis-copy.ts) reads the active language.

export type AnalysisCopyKey = keyof typeof EN;
export type AnalysisCatalog = typeof EN;

const EN = {
  // Screen titles and navigation.
  screenTitleManager: "Analyses · {product}",
  screenTitleCreate: "New Analysis · {product}",
  screenTitleDetail: "Analysis · {product}",
  navAnalyses: "Analyses",
  businessFinder: "Business Finder",
  accountsAnalyzer: "Accounts Analyzer",
  // Statuses.
  statusNotStarted: "Not started",
  statusRunning: "Running",
  statusPaused: "Paused",
  statusCompleted: "Completed",
  statusOutOfCredits: "Out of credits",
  statusError: "Error",
  statusDeprecated: "Deprecated",
  // Relative time.
  agoYear: { one: "1 year ago", other: "{count} years ago" },
  agoMonth: { one: "1 month ago", other: "{count} months ago" },
  agoDay: { one: "1 day ago", other: "{count} days ago" },
  agoHour: { one: "1 hour ago", other: "{count} hours ago" },
  agoMinute: { one: "1 minute ago", other: "{count} minutes ago" },
  justNow: "just now",
  // Shared buttons.
  cancel: "Cancel",
  save: "Save",
  delete: "Delete",
  open: "Open",
  pause: "Pause",
  start: "Start",
  resume: "Resume",
  rename: "Rename",
  viewExports: "View exports",
  // The progress panel.
  progress: "Progress",
  advancedView: "Advanced View",
  totalUsers: "Total Users",
  profilesAnalyzed: "Profiles Analyzed",
  usedCredits: "Used Credits",
  creditsUsed: { one: "1 credit", other: "{count} credits" },
  emails: "Emails",
  phones: "Phones",
  privateAccounts: "Private Accounts",
  publicAccounts: "{count} public",
  completion: "Completion",
  workingTitle: "We are working on it",
  workingDescription: "You can close this window if you want: the analysis keeps going in the background.",
  finishedTitle: "Process has finished successfully!",
  finishedDescription: "Download the data or send campaigns from the section below.",
  trialUsedTitle: "You've used up your trial credits 🎉",
  planUsedTitle: "You've used up the credits in your plan",
  trialUsedBody: {
    one: "The 1 contact you already extracted is yours and it's safe. You can download it right now from Exports. To keep extracting leads and unlock email sending, upgrade to a plan.",
    other:
      "The {count} contacts you already extracted are yours and they're safe. You can download them right now from Exports. To keep extracting leads and unlock email sending, upgrade to a plan.",
  },
  planUsedBody: {
    one: "The 1 contact you already extracted is yours and it's safe. You can download it right now from Exports. To keep extracting leads, add credits or move up a plan.",
    other:
      "The {count} contacts you already extracted are yours and they're safe. You can download them right now from Exports. To keep extracting leads, add credits or move up a plan.",
  },
  seePlans: "See plans",
  addCredits: "Add credits",
  creditLimitReached: "Credit limit reached ({used}/{limit})",
  creditLimitHint: "Increase the limit or remove it to continue.",
  adjustLimit: "Adjust limit",
  pausedTitle: "Analysis paused",
  pausedDescription: "Click «Resume» to continue.",
  sourceComingSoonTitle: "This source is coming soon",
  sourceComingSoonDescription:
    "This analysis can't run for now. What it already found stays here. To find more, start one on an account's followers or following.",
  // The manager.
  dashboard: "Dashboard",
  home: "Home",
  analysisManager: "Analysis Manager",
  overview: "Overview",
  newAnalysis: "New analysis",
  welcomeTitle: "Welcome to {product}!",
  welcomeDescription: "You're a click away from unleashing the sales beast within your business.",
  createFirst: "Create my first analysis",
  analyses: "Analyses",
  searchAnalyses: "Search analyses",
  bulkActions: "Bulk actions",
  selectedCount: "{count} selected",
  moveToProject: "Move to project…",
  moveToQueue: "Move to queue",
  addToQueue: "Add to queue",
  queuePremium: "Analysis Queue is a premium add-on",
  status: "Status",
  filters: "Filters",
  analysisName: "Analysis name",
  createdAgo: "Created {when}",
  leadsAnalysed: "Leads analysed",
  // The home's "Latest analyses" column (S39.01).
  latestTitle: "Latest analyses",
  latestSeeAll: "See all",
  latestLeads: { one: "1 lead", other: "{count} leads" },
  latestEmptyTitle: "No analyses yet",
  latestEmptyDescription: "Start one to find leads among an account's followers.",
  latestStart: "Start an analysis",
  latestErrorTitle: "We couldn't load your analyses",
  latestErrorDescription: "Check your connection, then try again.",
  latestRetry: "Try again",
  actions: "Actions",
  actionsFor: "Actions for {name}",
  showingResults: "Showing {shown} of {total} results",
  // One analysis.
  notFoundTitle: "Analysis not found",
  notFoundDescription:
    "The analysis you are looking for doesn't exist or you don't have permission to access it.",
  goToManager: "Go to Analysis Manager",
  options: "Options",
  settings: "Settings",
  settingsSaved: "Settings saved successfully",
  couldNotSaveSettings: "Could not save the settings",
  autoEnrichLabel: "Auto Enrich 💎",
  // Dialogs.
  deleteTitle: "Are you sure you want to delete this analysis?",
  deleteDescription: "All associated prospects will also be deleted. This cannot be undone.",
  renameTitle: "Rename analysis",
  renameField: "Enter a new name for the analysis",
  moveTitle: { one: "Move 1 item to a project", other: "Move {count} items to a project" },
  move: "Move",
  selectProject: "Select project",
  // Outcomes of the actions.
  paused: "Analysis paused successfully",
  pauseFailed: "Could not pause the analysis",
  renamed: "Analysis renamed successfully!",
  renameFailed: "Could not rename the analysis",
  deleted: { one: "Analysis deleted", other: "{count} analyses deleted" },
  deleteFailed: "Could not delete",
  moved: "{count} moved to the project",
  moveFailed: "Could not move to the project",
} as const;

const ES: Translation<AnalysisCatalog> = {
  screenTitleManager: "Análisis · {product}",
  screenTitleCreate: "Nuevo análisis · {product}",
  screenTitleDetail: "Análisis · {product}",
  navAnalyses: "Análisis",
  businessFinder: "Business Finder",
  accountsAnalyzer: "Accounts Analyzer",
  statusNotStarted: "Sin iniciar",
  statusRunning: "En curso",
  statusPaused: "En pausa",
  statusCompleted: "Completado",
  statusOutOfCredits: "Sin créditos",
  statusError: "Error",
  statusDeprecated: "Obsoleto",
  agoYear: { one: "hace 1 año", other: "hace {count} años" },
  agoMonth: { one: "hace 1 mes", other: "hace {count} meses" },
  agoDay: { one: "hace 1 día", other: "hace {count} días" },
  agoHour: { one: "hace 1 hora", other: "hace {count} horas" },
  agoMinute: { one: "hace 1 minuto", other: "hace {count} minutos" },
  justNow: "justo ahora",
  cancel: "Cancelar",
  save: "Guardar",
  delete: "Eliminar",
  open: "Abrir",
  pause: "Pausar",
  start: "Iniciar",
  resume: "Reanudar",
  rename: "Renombrar",
  viewExports: "Ver exportaciones",
  progress: "Progreso",
  advancedView: "Vista avanzada",
  totalUsers: "Usuarios totales",
  profilesAnalyzed: "Perfiles analizados",
  usedCredits: "Créditos usados",
  creditsUsed: { one: "1 crédito", other: "{count} créditos" },
  emails: "Correos",
  phones: "Teléfonos",
  privateAccounts: "Cuentas privadas",
  publicAccounts: "{count} públicas",
  completion: "Avance",
  workingTitle: "Estamos en ello",
  workingDescription: "Puedes cerrar esta ventana: el análisis sigue en segundo plano.",
  finishedTitle: "¡El proceso terminó con éxito!",
  finishedDescription: "Descarga los datos o envía campañas desde la sección de abajo.",
  trialUsedTitle: "Usaste todos tus créditos de prueba 🎉",
  planUsedTitle: "Usaste todos los créditos de tu plan",
  trialUsedBody: {
    one: "El contacto que ya extrajiste es tuyo y está a salvo. Puedes descargarlo ahora mismo desde Exportaciones. Para seguir extrayendo leads y activar el envío de correos, pasa a un plan.",
    other:
      "Los {count} contactos que ya extrajiste son tuyos y están a salvo. Puedes descargarlos ahora mismo desde Exportaciones. Para seguir extrayendo leads y activar el envío de correos, pasa a un plan.",
  },
  planUsedBody: {
    one: "El contacto que ya extrajiste es tuyo y está a salvo. Puedes descargarlo ahora mismo desde Exportaciones. Para seguir extrayendo leads, agrega créditos o sube de plan.",
    other:
      "Los {count} contactos que ya extrajiste son tuyos y están a salvo. Puedes descargarlos ahora mismo desde Exportaciones. Para seguir extrayendo leads, agrega créditos o sube de plan.",
  },
  seePlans: "Ver planes",
  addCredits: "Agregar créditos",
  creditLimitReached: "Se alcanzó el límite de créditos ({used}/{limit})",
  creditLimitHint: "Sube el límite o quítalo para continuar.",
  adjustLimit: "Ajustar límite",
  pausedTitle: "Análisis en pausa",
  pausedDescription: "Pulsa «Reanudar» para continuar.",
  sourceComingSoonTitle: "Esta fuente llega pronto",
  sourceComingSoonDescription:
    "Este análisis no puede ejecutarse por ahora. Lo que ya encontró se queda aquí. Para encontrar más, empieza uno sobre los seguidores o seguidos de una cuenta.",
  dashboard: "Panel",
  home: "Inicio",
  analysisManager: "Gestor de análisis",
  overview: "Resumen",
  newAnalysis: "Nuevo análisis",
  welcomeTitle: "¡Te damos la bienvenida a {product}!",
  welcomeDescription: "Estás a un clic de liberar la fuerza de ventas de tu negocio.",
  createFirst: "Crear mi primer análisis",
  analyses: "Análisis",
  searchAnalyses: "Buscar análisis",
  bulkActions: "Acciones en lote",
  selectedCount: "{count} seleccionados",
  moveToProject: "Mover a un proyecto…",
  moveToQueue: "Mover a la cola",
  addToQueue: "Agregar a la cola",
  queuePremium: "La cola de análisis es un complemento premium",
  status: "Estado",
  filters: "Filtros",
  analysisName: "Nombre del análisis",
  createdAgo: "Creado {when}",
  leadsAnalysed: "Leads analizados",
  latestTitle: "Últimos análisis",
  latestSeeAll: "Ver todos",
  latestLeads: { one: "1 lead", other: "{count} leads" },
  latestEmptyTitle: "Aún no tienes análisis",
  latestEmptyDescription: "Inicia uno para encontrar leads entre los seguidores de una cuenta.",
  latestStart: "Inicia un análisis",
  latestErrorTitle: "No pudimos cargar tus análisis",
  latestErrorDescription: "Revisa tu conexión y vuelve a intentarlo.",
  latestRetry: "Reintentar",
  actions: "Acciones",
  actionsFor: "Acciones para {name}",
  showingResults: "Mostrando {shown} de {total} resultados",
  notFoundTitle: "No encontramos el análisis",
  notFoundDescription: "El análisis que buscas no existe o no tienes permiso para verlo.",
  goToManager: "Ir al gestor de análisis",
  options: "Opciones",
  settings: "Ajustes",
  settingsSaved: "Ajustes guardados",
  couldNotSaveSettings: "No pudimos guardar los ajustes",
  autoEnrichLabel: "Auto Enrich 💎",
  deleteTitle: "¿Seguro que quieres eliminar este análisis?",
  deleteDescription: "También se eliminarán todos los prospectos asociados. No se puede deshacer.",
  renameTitle: "Renombrar análisis",
  renameField: "Escribe un nuevo nombre para el análisis",
  moveTitle: { one: "Mover 1 elemento a un proyecto", other: "Mover {count} elementos a un proyecto" },
  move: "Mover",
  selectProject: "Elige un proyecto",
  paused: "Análisis en pausa",
  pauseFailed: "No pudimos pausar el análisis",
  renamed: "Análisis renombrado",
  renameFailed: "No pudimos renombrar el análisis",
  deleted: { one: "Análisis eliminado", other: "{count} análisis eliminados" },
  deleteFailed: "No pudimos eliminar",
  moved: "{count} movidos al proyecto",
  moveFailed: "No pudimos mover al proyecto",
};

export const ANALYSIS_CATALOGS: Catalogs<AnalysisCatalog> = { en: EN, es: ES };

export const STATUS_LABEL: Readonly<Record<AnalysisStatus, PlainKey<AnalysisCatalog>>> = {
  "not-started": "statusNotStarted",
  running: "statusRunning",
  paused: "statusPaused",
  completed: "statusCompleted",
  "out-of-credits": "statusOutOfCredits",
  error: "statusError",
  deprecated: "statusDeprecated",
};

export const STATUS_TONE: Readonly<Record<AnalysisStatus, Tone>> = {
  "not-started": "neutral",
  running: "primary",
  paused: "warning",
  completed: "success",
  "out-of-credits": "danger",
  error: "danger",
  deprecated: "neutral",
};

export const STATUS_ORDER: readonly AnalysisStatus[] = [
  "running",
  "completed",
  "not-started",
  "paused",
  "out-of-credits",
  "error",
  "deprecated",
];

export function screenTitle(platform: Platform | null, t: Translate<AnalysisCatalog>): string {
  return platform === "google-maps" ? t("businessFinder") : t("accountsAnalyzer");
}

/** "3 minutes ago", "6 months ago": relative to the reader's clock. */
export function timeAgo(iso: string, params: { now: number; t: Translate<AnalysisCatalog> }): string {
  const seconds = Math.max(0, Math.round((params.now - Date.parse(iso)) / 1000));
  const units = [
    [31_536_000, "agoYear"],
    [2_592_000, "agoMonth"],
    [86_400, "agoDay"],
    [3_600, "agoHour"],
    [60, "agoMinute"],
  ] as const;
  for (const [size, key] of units) {
    const count = Math.floor(seconds / size);
    if (count >= 1) return params.t(key, { count });
  }
  return params.t("justNow");
}
